import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  type ValidationErrors,
  type ValidatorFn,
} from '@angular/forms';

declare module '@angular/forms' {
  interface AbstractControl {
    /**
     * Non-blocking counterpart of `errors`: recomputed alongside them from
     * `warningValidator`, but never makes the control invalid.
     *
     * Only set once the control has run its validators at least once.
     */
    warnings?: ValidationErrors | null;

    /**
     * Purely informative counterpart of `errors`, recomputed from
     * `infoValidator`. Unlike warnings it is displayed even without a value.
     *
     * Only set once the control has run its validators at least once.
     */
    info?: ValidationErrors | null;

    /** Feeds {@link AbstractControl.warnings}, set by `QuestionControlService`. */
    warningValidator?: ValidatorFn | null;

    /** Feeds {@link AbstractControl.info}, set by `QuestionControlService`. */
    infoValidator?: ValidatorFn | null;

    setWarnings(warnings: ValidationErrors | null): void;

    setInfo(info: ValidationErrors | null): void;

    /** Marks the root of a form built by `QuestionControlService`. */
    __isMyreForm?: true;

    /**
     * Pristine state remembered across a disable/enable cycle: Angular marks a
     * control dirty when it is re-enabled, which would flag untouched fields.
     */
    __myre_pristine?: boolean;
  }
}

/**
 * Angular internals the warning/info layer hooks into. `_runValidator` is the
 * single place where a control recomputes `errors`, so it is also where
 * `warnings` and `info` have to be recomputed.
 */
interface AbstractControlInternals {
  _runValidator: (
    this: AbstractControl,
    ...args: readonly unknown[]
  ) => ValidationErrors | null;
}

/**
 * `_allControlsDisabled` walks children on a group, which a form array of
 * plain controls must not do.
 */
interface ControlContainerInternals {
  _allControlsDisabled: (this: AbstractControl) => boolean;
}

let installed = false;

/**
 * Install the prototype patches backing the augmentation above.
 *
 * `QuestionControlService` calls it: workspace packages are generated with
 * `"sideEffects": false`, so a bare `import` of this module is tree-shaken away
 * and the patches never run.
 */
export function installAngularFormsAugmentation(): void {
  if (installed) {
    return;
  }

  installed = true;

  const controlInternals =
    AbstractControl.prototype as unknown as AbstractControlInternals;

  const runValidator = controlInternals._runValidator;

  controlInternals._runValidator = function (
    this: AbstractControl,
    ...args: readonly unknown[]
  ): ValidationErrors | null {
    const ret = runValidator.apply(this, args);

    this.warnings = this.warningValidator ? this.warningValidator(this) : null;
    this.info = this.infoValidator ? this.infoValidator(this) : null;

    return ret;
  };

  AbstractControl.prototype.setWarnings = function (
    this: AbstractControl,
    warnings: ValidationErrors | null,
  ): void {
    this.warnings = warnings;
  };

  AbstractControl.prototype.setInfo = function (
    this: AbstractControl,
    info: ValidationErrors | null,
  ): void {
    this.info = info;
  };

  (
    UntypedFormArray.prototype as unknown as ControlContainerInternals
  )._allControlsDisabled = (
    UntypedFormControl.prototype as unknown as ControlContainerInternals
  )._allControlsDisabled;
}
